import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AuftragsAkteRechnung, Partner, Rechnungsart } from '../types';
import { fehlerText } from '../lib/belegKarte';
import { kannZuPartnerNavigieren, navigiereZuPartner } from '../lib/navigation';
import {
  abschlagOptionen, abschlagVorschau, alsDezimalString, artLabel, auftragNettoCent, centText, datumDe,
  inCent, liegenGelasseneAbschlaege, normalisiereArt, pflichtHinweisAktiv, zahlbetragVorschau,
} from '../lib/rechnungsart';
import './RechnungsartDialoge.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

type ErstellbareArt = Extract<Rechnungsart, 'RECHNUNG' | 'ABSCHLAG' | 'SCHLUSS'>;

const ARTEN: Array<{ art: ErstellbareArt; label: string; hilfe: string }> = [
  { art: 'RECHNUNG', label: 'Rechnung', hilfe: 'Rechnung über den gesamten Auftrag.' },
  { art: 'ABSCHLAG', label: 'Abschlagsrechnung', hilfe: 'Teilforderung während der Ausführung (fester Betrag oder Prozent).' },
  { art: 'SCHLUSS', label: 'Schlussrechnung', hilfe: 'Rechnet den Auftrag ab und verrechnet bezahlte Abschläge.' },
];

interface Props {
  auftragId: number;
  kundeId: number | null;
  /** Summe der Auftragspositionen (netto, vor Gesamtrabatt) — Grundlage der Vorschau. */
  gesamtsumme: number | null;
  gesamtrabattProzent: number | null;
  /** Beleg-Steuersatz des Auftrags (Override) in Prozent; null = je Artikel (Vorschau mit 19 %). */
  steuersatzProzent: number | null;
  steuersatzName: string;
  /** Bisherige Rechnungen des Auftrags (aus der Auftragsakte). */
  rechnungen: AuftragsAkteRechnung[];
  /** Rechnung (Entwurf) wurde angelegt. */
  onErstellt: () => void;
  onAbbrechen: () => void;
}

const heuteIso = () => new Date().toISOString().slice(0, 10);

/** Dialog „Rechnung erstellen“ in der Auftragsakte: Rechnungsart, Leistungszeitpunkt (§ 14 Abs. 4 Nr. 6 UStG),
    bei Abschlag Betrag/Prozent mit Vorschau, bei Schluss die Checkliste bezahlter Abschläge. Zeigt einen nicht
    blockierenden Hinweis, wenn der Kunde Unternehmer ohne E-Rechnungsformat ist und der Pflicht-Stichtag erreicht ist. */
const RechnungErstellenDialog: React.FC<Props> = ({
  auftragId, kundeId, gesamtsumme, gesamtrabattProzent, steuersatzProzent, steuersatzName,
  rechnungen, onErstellt, onAbbrechen,
}) => {
  const [art, setArt] = useState<ErstellbareArt>('RECHNUNG');
  const [leistungsArt, setLeistungsArt] = useState<'datum' | 'zeitraum'>('datum');
  const [leistungsdatum, setLeistungsdatum] = useState(heuteIso);
  const [leistungVon, setLeistungVon] = useState('');
  const [leistungBis, setLeistungBis] = useState('');
  const [modus, setModus] = useState<'betrag' | 'prozent'>('betrag');
  const [eingabe, setEingabe] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [duplikat, setDuplikat] = useState<{ nummer: string; status: string } | null>(null);

  // Pflicht-Hinweis: Kunde (Unternehmer? Format?) und Stichtag der Firma. /api/firma ist nur für
  // Admin/Geschäftsführer lesbar — für andere Rollen bleibt der Stichtag unbekannt, der Hinweis aus.
  const [kunde, setKunde] = useState<Partner | null>(null);
  const [firma, setFirma] = useState<{ erechnung_pflicht_ab?: string; kleinunternehmer?: boolean } | null>(null);
  useEffect(() => {
    let aktiv = true;
    if (kundeId != null) {
      axios.get<Partner>(`${API_URL}/api/partners/${kundeId}`)
        .then(r => { if (aktiv) setKunde(r.data); })
        .catch(() => { if (aktiv) setKunde(null); });
    }
    axios.get(`${API_URL}/api/firma`)
      .then(r => { if (aktiv) setFirma(r.data); })
      .catch(() => { if (aktiv) setFirma(null); });
    return () => { aktiv = false; };
  }, [kundeId]);

  const stichtag = firma?.erechnung_pflicht_ab ?? null;
  const zeigePflichtHinweis = pflichtHinweisAktiv({
    unternehmer: kunde?.unternehmer,
    eRechnungFormat: kunde?.e_rechnung_format,
    landCode: kunde?.land_code,
    stichtag,
    kleinunternehmer: firma?.kleinunternehmer,
    heute: heuteIso(),
  });

  const satz = steuersatzProzent ?? 19;
  const basisNetto = useMemo(
    () => auftragNettoCent(gesamtsumme, gesamtrabattProzent), [gesamtsumme, gesamtrabattProzent]);
  const bisherAbschlagNetto = useMemo(() => rechnungen
    .filter(r => normalisiereArt(r.rechnungsart) === 'ABSCHLAG' && r.status !== 'STORNIERT')
    .reduce((s, r) => s + (inCent(r.netto_gesamt) ?? 0), 0), [rechnungen]);
  const optionen = useMemo(() => abschlagOptionen(rechnungen), [rechnungen]);
  const bestehendeSchluss = rechnungen.find(
    r => normalisiereArt(r.rechnungsart) === 'SCHLUSS' && r.status !== 'STORNIERT');

  const waehleArt = (neu: ErstellbareArt) => {
    setArt(neu);
    setFehler(null);
    setDuplikat(null);
    // Schlussrechnung: alle wählbaren Abschläge vorbelegen — sichtbar in der Checkliste, abwählbar.
    if (neu === 'SCHLUSS') {
      setGewaehlt(new Set(optionen.filter(o => o.auswaehlbar).map(o => o.rechnung.ausgangsrechnung_id)));
    }
  };

  const vorschau = art === 'ABSCHLAG'
    ? abschlagVorschau({ modus, eingabe, basisNettoCent: basisNetto, bisherNettoCent: bisherAbschlagNetto, satz })
    : null;
  const gewaehlteBrutto = optionen
    .filter(o => gewaehlt.has(o.rechnung.ausgangsrechnung_id))
    .map(o => o.rechnung.brutto_gesamt);
  const zahlbetrag = art === 'SCHLUSS' ? zahlbetragVorschau(basisNetto, satz, gewaehlteBrutto) : null;
  // Wie der Server (409): jeder nicht stornierte Abschlag muss in der Schlussrechnung verrechnet werden.
  const liegenGelassen = art === 'SCHLUSS' ? liegenGelasseneAbschlaege(rechnungen, gewaehlt) : [];
  const gesperrt = liegenGelassen.length > 0;

  const erstellen = async (trotzdem = false) => {
    setFehler(null);
    setDuplikat(null);
    if (leistungsArt === 'zeitraum' && (!leistungVon || !leistungBis)) {
      setFehler('Bitte Anfang und Ende des Leistungszeitraums angeben.');
      return;
    }
    const body: Record<string, unknown> = {
      auftrag_id: auftragId,
      rechnungsart: art,
      ...(leistungsArt === 'datum'
        ? { leistungsdatum: leistungsdatum || null }
        : { leistungszeitraum_von: leistungVon, leistungszeitraum_bis: leistungBis }),
      ...(trotzdem ? { trotzdem: true } : {}),
    };
    if (art === 'ABSCHLAG') {
      const wert = alsDezimalString(eingabe);
      if (wert === null) {
        setFehler(modus === 'betrag'
          ? 'Bitte einen Abschlagsbetrag (netto) größer 0 angeben.'
          : 'Bitte einen Prozentsatz größer 0 angeben.');
        return;
      }
      body[modus === 'betrag' ? 'abschlag_betrag' : 'abschlag_prozent'] = wert;
    }
    if (art === 'SCHLUSS') {
      if (gesperrt) return;
      if (gewaehlt.size === 0) {
        setFehler('Bitte mindestens einen bezahlten Abschlag zur Verrechnung wählen – ohne Abschläge genügt eine normale Rechnung.');
        return;
      }
      body.abschlag_ids = Array.from(gewaehlt);
    }
    setLaedt(true);
    try {
      await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen`, body);
      onErstellt();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      const kandidaten = axios.isAxiosError(err) && err.response?.status === 409 ? detail?.kandidaten : undefined;
      if (Array.isArray(kandidaten) && kandidaten.length > 0) {
        setDuplikat({
          nummer: String(kandidaten[0].nummer_anzeige ?? ''),
          status: String(kandidaten[0].status ?? ''),
        });
      } else {
        setFehler(fehlerText(detail, `${artLabel(art)} konnte nicht erstellt werden.`));
      }
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div className="rd-dialog">
      {zeigePflichtHinweis && (
        <div className="rd-banner" role="status">
          <div className="k-alert k-alert--warn">
            <div className="k-alert__title">E-Rechnung für diesen Kunden einrichten</div>
            <div className="k-alert__msg">
              {kunde?.name ?? 'Der Kunde'} ist als Unternehmer geführt, hat aber kein E-Rechnungsformat. Seit dem{' '}
              {datumDe(stichtag)} gilt für Rechnungen an inländische Unternehmer die E-Rechnungspflicht
              (ZUGFeRD oder XRechnung). Sie können die Rechnung trotzdem erstellen; das Format lässt sich
              in den Kunden-Stammdaten hinterlegen.
              {kundeId != null && kannZuPartnerNavigieren() && (
                <>
                  {' '}
                  <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={() => navigiereZuPartner(kundeId)}>
                    Kunden öffnen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
        <legend className="rd-hinweis" style={{ padding: 0, marginBottom: 4 }}>Rechnungsart</legend>
        <div className="rd-arten">
          {ARTEN.map(a => (
            <label key={a.art} title={a.hilfe}>
              <input type="radio" name="rechnungsart" checked={art === a.art} onChange={() => waehleArt(a.art)} />
              {a.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="rd-hinweis">{ARTEN.find(a => a.art === art)?.hilfe}</div>

      {art === 'ABSCHLAG' && (
        <>
          <div className="rd-zeile">
            <label>
              <input type="radio" name="abschlagModus" checked={modus === 'betrag'}
                     onChange={() => { setModus('betrag'); setEingabe(''); }} />
              Fester Betrag (netto)
            </label>
            <label>
              <input type="radio" name="abschlagModus" checked={modus === 'prozent'}
                     onChange={() => { setModus('prozent'); setEingabe(''); }} />
              Prozent der Auftragssumme
            </label>
          </div>
          <div className="rd-zeile">
            <input
              type="text"
              inputMode="decimal"
              className="rd-feld"
              aria-label={modus === 'betrag' ? 'Abschlagsbetrag netto' : 'Abschlag in Prozent'}
              placeholder={modus === 'betrag' ? '0,00' : '30'}
              value={eingabe}
              onChange={e => setEingabe(e.target.value)}
            />
            <span>{modus === 'betrag' ? '€ netto' : '%'}</span>
            {basisNetto !== null && (
              <span className="rd-hinweis">
                Auftragssumme {centText(basisNetto)} netto (ohne Metallzuschläge), bisher per Abschlag abgerechnet {centText(bisherAbschlagNetto)}
                {' '}– noch offen {centText(basisNetto - bisherAbschlagNetto)} netto
              </span>
            )}
          </div>
          {vorschau && (
            <div className="rd-vorschau" aria-live="polite">
              <div className="rd-vorschau__zeile"><span>Netto</span><span>{centText(vorschau.nettoCent)}</span></div>
              <div className="rd-vorschau__zeile"><span>USt {satz.toLocaleString('de-DE')} %</span><span>{centText(vorschau.mwstCent)}</span></div>
              <div className="rd-vorschau__zeile rd-vorschau__zeile--summe"><span>Brutto</span><span>{centText(vorschau.bruttoCent)}</span></div>
              {vorschau.restNettoCent !== null && !vorschau.uebersteigt && (
                <div className="rd-hinweis">Danach noch nicht per Abschlag abgerechnet: {centText(vorschau.restNettoCent)} netto.</div>
              )}
              {vorschau.uebersteigt && (
                <div className="rd-vorschau__warnung">
                  Der Abschlag übersteigt voraussichtlich den noch offenen Auftragswert (Vorschau ohne Metallzuschläge) –
                  das System prüft das beim Anlegen und lehnt ihn gegebenenfalls ab.
                </div>
              )}
              <div className="rd-hinweis">
                Vorschau mit einheitlichem Steuersatz; die endgültige Steuer folgt den Auftragspositionen.
              </div>
            </div>
          )}
          {!vorschau && basisNetto === null && modus === 'prozent' && (
            <div className="rd-hinweis">Die Auftragssumme ist hier nicht sichtbar – der Betrag wird beim Anlegen berechnet.</div>
          )}
        </>
      )}

      {art === 'SCHLUSS' && (
        <>
          {bestehendeSchluss && (
            <div className="rd-hinweis">
              Zu diesem Auftrag gibt es bereits die Schlussrechnung {bestehendeSchluss.nummer_anzeige}.
              Abschläge, die dort verrechnet sind, lehnt das System ab.
            </div>
          )}
          {optionen.length === 0 ? (
            <div className="rd-hinweis">
              Zu diesem Auftrag gibt es keine Abschlagsrechnungen – eine Schlussrechnung ist nur nach
              Abschlägen sinnvoll. Wählen Sie „Rechnung“.
            </div>
          ) : (
            <table className="rd-tabelle">
              <thead>
                <tr><th></th><th>Abschlagsrechnung</th><th>Status</th><th className="num">Brutto</th></tr>
              </thead>
              <tbody>
                {optionen.map(o => {
                  const id = o.rechnung.ausgangsrechnung_id;
                  return (
                    <tr key={id} className={o.auswaehlbar ? undefined : 'rd-gesperrt'}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${o.rechnung.nummer_anzeige} verrechnen`}
                          checked={gewaehlt.has(id)}
                          disabled={!o.auswaehlbar}
                          onChange={e => setGewaehlt(g => {
                            const neu = new Set(g);
                            if (e.target.checked) neu.add(id); else neu.delete(id);
                            return neu;
                          })}
                        />
                      </td>
                      <td>
                        {o.rechnung.nummer_anzeige}
                        {o.grund && <span className="rd-grund">Nicht wählbar: {o.grund}</span>}
                      </td>
                      <td>{o.rechnung.status}</td>
                      <td className="num">{centText(inCent(o.rechnung.brutto_gesamt) ?? 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {zahlbetrag && gewaehlt.size > 0 && (
            <div className="rd-vorschau" aria-live="polite">
              <div className="rd-vorschau__zeile"><span>Gesamtbetrag der Leistung (brutto, ca.)</span><span>{centText(zahlbetrag.gesamtBruttoCent)}</span></div>
              <div className="rd-vorschau__zeile"><span>abzüglich bezahlter Abschläge (brutto)</span><span>−{centText(zahlbetrag.abzugBruttoCent)}</span></div>
              <div className="rd-vorschau__zeile rd-vorschau__zeile--summe"><span>Zahlbetrag nach Abzug (ca.)</span><span>{centText(zahlbetrag.zahlbetragCent)}</span></div>
              <div className="rd-hinweis">
                Vorschau ohne Metallzuschläge und mit einheitlichem Steuersatz; die Schlussrechnung weist den genauen Betrag aus.
              </div>
            </div>
          )}
          {gesperrt && (
            <div className="k-alert k-alert--warn" role="alert">
              <div className="k-alert__title">Schlussrechnung noch nicht möglich</div>
              <div className="k-alert__msg">
                Die Schlussrechnung berechnet die volle Leistung. Diese Abschläge würden nicht verrechnet und
                blieben zusätzlich offen – der Kunde würde sie doppelt bezahlen:{' '}
                {liegenGelassen.map(r => `${r.nummer_anzeige} (${r.status})`).join(', ')}.
                Bitte zuerst bezahlen lassen und auswählen oder stornieren (Entwürfe löschen).
              </div>
            </div>
          )}
          <div className="rd-hinweis">
            Nur bezahlte oder teilbezahlte Abschläge lassen sich verrechnen – und alle nicht stornierten
            Abschläge des Auftrags müssen verrechnet werden.
          </div>
        </>
      )}

      <div className="rd-zeile">
        <label><input type="radio" name="leistungsArt" checked={leistungsArt === 'datum'} onChange={() => setLeistungsArt('datum')} /> Leistungsdatum</label>
        <label><input type="radio" name="leistungsArt" checked={leistungsArt === 'zeitraum'} onChange={() => setLeistungsArt('zeitraum')} /> Leistungszeitraum</label>
      </div>
      {leistungsArt === 'datum' ? (
        <div className="rd-zeile">
          <input type="date" value={leistungsdatum} onChange={e => setLeistungsdatum(e.target.value)} />
          <span className="rd-hinweis">Leer lassen = Leistungsdatum entspricht Rechnungsdatum.</span>
        </div>
      ) : (
        <div className="rd-zeile">
          <input type="date" value={leistungVon} onChange={e => setLeistungVon(e.target.value)} />
          <span>bis</span>
          <input type="date" value={leistungBis} onChange={e => setLeistungBis(e.target.value)} />
        </div>
      )}

      {fehler && <div className="rd-fehler" role="alert">{fehler}</div>}
      {duplikat && (
        <div className="rd-fehler" role="alert">
          Zum Auftrag existiert bereits Rechnung {duplikat.nummer} ({duplikat.status}). Trotzdem anlegen?{' '}
          <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={() => erstellen(true)} disabled={laedt}>
            Trotzdem anlegen
          </button>
        </div>
      )}

      <div className="rd-zeile">
        <span className="rd-hinweis">
          Steuersatz: {steuersatzName}
        </span>
        <button type="button" className="k-btn k-btn--primary k-btn--sm" onClick={() => erstellen()}
                disabled={laedt || gesperrt} title={gesperrt ? 'Erst alle Abschläge bezahlen lassen und auswählen oder stornieren' : undefined}>
          {laedt ? 'Erstellt…' : `${artLabel(art)} erstellen`}
        </button>
        <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={onAbbrechen} disabled={laedt}>
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default RechnungErstellenDialog;
